import cx from "classnames";
import { Input } from "component-library";
import { useTranslation } from "i18n";
import { useProfileTheme } from "pages/Profile/hooks/useProfileTheme";
import { Theme } from "shared-types";
import EditButton from "pages/Profile/components/common/EditButton";
import EditSectionActions from "pages/Profile/components/ProfileHeader/components/EditSectionActions";
import type { IPronounsPronunciation } from "pages/Profile/hooks/useGetPronounsPronunciation";
import { MAX_PRONOUNS_PRONUNCIATION_CHAR_LENGTH } from "../../constants";

export type PronounsPronunciationLayout = "inline" | "block";

interface PronounsPronunciationSectionProps {
  layout: PronounsPronunciationLayout;
  pronounsDisplay: string;
  pronunciationDisplay: string;
  hasPandP: boolean;
  editingPandP: boolean;
  draftPandP: IPronounsPronunciation;
  pandPErrors: { pronouns?: string; pronunciation?: string };
  pAndPSaveDisabled: boolean;
  isSavingPandP: boolean;
  isEdit?: boolean;
  isPublicView: boolean;
  onOpenEdit: () => void;
  onCloseEdit: () => void;
  onSave: () => void;
  onDraftChange: (draft: IPronounsPronunciation) => void;
}

const BLOCK_LAYOUT_CLASS =
  "col-span-2 row-start-2 min-w-0 md:col-auto md:row-auto md:mt-0 mb-2";

const PronounsPronunciationSection = ({
  layout,
  pronounsDisplay,
  pronunciationDisplay,
  hasPandP,
  editingPandP,
  draftPandP,
  pandPErrors,
  pAndPSaveDisabled,
  isSavingPandP,
  isEdit,
  isPublicView,
  onOpenEdit,
  onCloseEdit,
  onSave,
  onDraftChange,
}: PronounsPronunciationSectionProps) => {
  const { t } = useTranslation();
  const [theme] = useProfileTheme();
  const isDark = theme === Theme.Dark;
  const isBlockLayout = layout === "block";
  const isInlineLayout = layout === "inline";

  const showSection = hasPandP || !isPublicView;

  if (!showSection) return null;

  if (editingPandP) {
    if (isInlineLayout) return null;

    return (
      <div
        className={cx(
          "profile-header-edit-section mb-2 flex w-full max-w-full flex-col rounded-[0.625rem] p-0 md:mb-0 md:max-w-md",
          "font-tertiary text-s font-normal leading-[1.4219rem] text-color-neutral-one dark:text-white/90",
          { "profile-header-edit-section--dark": isDark },
          !isDark && "text-grey-void",
          isBlockLayout && BLOCK_LAYOUT_CLASS
        )}
      >
        <div className="flex flex-col gap-2 md:mt-2">
          <div className={cx("min-w-0", pandPErrors.pronouns && "pb-7")}>
            <Input
              type="text"
              label={t("performer:profile.header.pronouns")}
              placeholder={t("performer:profile.header.pronounsPlaceholder")}
              value={draftPandP.pronouns}
              onChange={(e) =>
                onDraftChange({
                  ...draftPandP,
                  pronouns: e.target.value,
                })
              }
              maxLength={MAX_PRONOUNS_PRONUNCIATION_CHAR_LENGTH}
              invalid={Boolean(pandPErrors.pronouns)}
              validationMessage={pandPErrors.pronouns}
              isFormSubmitted={true}
              disabled={isSavingPandP}
            />
          </div>
          <div className={cx("min-w-0", pandPErrors.pronunciation && "pb-8")}>
            <Input
              type="text"
              label={t("performer:profile.header.pronunciation")}
              placeholder={t(
                "performer:profile.header.pronunciationPlaceholder"
              )}
              value={draftPandP.pronunciation}
              onChange={(e) =>
                onDraftChange({
                  ...draftPandP,
                  pronunciation: e.target.value,
                })
              }
              maxLength={MAX_PRONOUNS_PRONUNCIATION_CHAR_LENGTH}
              invalid={Boolean(pandPErrors.pronunciation)}
              validationMessage={pandPErrors.pronunciation}
              isFormSubmitted={true}
              disabled={isSavingPandP}
            />
          </div>
        </div>
        <EditSectionActions
          onSave={onSave}
          onCancel={onCloseEdit}
          saveDisabled={pAndPSaveDisabled}
          saveLabel={t("common:button.label.save")}
          cancelLabel={t("common:button.label.cancel")}
          isSaving={isSavingPandP}
        />
      </div>
    );
  }

  const getPlaceholderTextForNoContent = () => {
    return isPublicView || !isEdit ? null : (
      <span className="text-s italic leading-[1.25rem] text-color-neutral-two dark:text-white/70">
        {t("performer:profile.header.addPronounsPronunciation")}
      </span>
    );
  };

  const displayContent = hasPandP ? (
    <>
      {pronounsDisplay}
      {pronounsDisplay && pronunciationDisplay && " | "}
      {pronunciationDisplay}
    </>
  ) : (
    getPlaceholderTextForNoContent()
  );

  const showEditIcon = isEdit && !isPublicView;

  if (layout === "inline") {
    return (
      <span className="hidden items-center gap-3 text-sm normal-case text-grey-void md:flex dark:text-white/70">
        {displayContent}
        {showEditIcon && (
          <EditButton
            onClick={onOpenEdit}
            className="text-black dark:text-white"
          />
        )}
      </span>
    );
  }

  return (
    <div
      className={cx(
        "flex w-full items-center gap-2 text-sm normal-case md:hidden",
        isBlockLayout && BLOCK_LAYOUT_CLASS
      )}
    >
      <span
        className={cx(
          "min-w-0",
          hasPandP
            ? "text-grey-void dark:text-white/70"
            : "text-color-neutral-two"
        )}
      >
        {displayContent}
      </span>
      {showEditIcon && (
        <EditButton
          onClick={onOpenEdit}
          className="shrink-0 text-black dark:text-white"
        />
      )}
    </div>
  );
};

export default PronounsPronunciationSection;
